/** Readiness never treats an unchecked category as complete. */
import { worstTenantHealthStatus, type TenantHealthOverview, type TenantHealthStatus } from "@common/types";
import { ADMIN_NAV } from "../../navigation/admin-nav";
export type SetupHealthStatus = TenantHealthStatus;

export function categoryHealth(statuses: SetupHealthStatus[]): SetupHealthStatus {
  return statuses.length ? worstTenantHealthStatus(statuses) : "unknown";
}

const STATUS_RANK: Record<SetupHealthStatus, number> = { failing: 0, attention: 1, unknown: 2, ready: 3, notApplicable: 4 };

/** Worst first: failing, attention, unknown, ready, notApplicable. Stable for equal statuses. */
export function sortChecksByStatus<T extends { status: SetupHealthStatus }>(checks: T[]): T[] {
  return checks.map((check, index) => ({ check, index }))
    .sort((a, b) => (STATUS_RANK[a.check.status] ?? 2) - (STATUS_RANK[b.check.status] ?? 2) || a.index - b.index)
    .map(value => value.check);
}

/** Known Azure portal destinations by stable code. Unknown codes yield no link. */
const PORTAL_LINKS: Record<string, string> = {
  azureFunctionApp: "https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Web%2Fsites",
  azureKeyVault: "https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.KeyVault%2Fvaults",
  azureCosmos: "https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.DocumentDb%2FdatabaseAccounts",
  azureStorage: "https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Storage%2FStorageAccounts",
  azureOpenAi: "https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.CognitiveServices%2Faccounts",
};

/** Either an admin route or a known external portal link; anything else is dropped. */
export function opsFixLink(fixHref: string | undefined, meta?: Record<string, string | number | boolean>): { href: string; external: boolean } | undefined {
  const internal = healthFixHref(fixHref);
  if (internal) return { href: internal, external: false };
  const code = typeof meta?.portal === "string" ? meta.portal : fixHref;
  const portal = code ? PORTAL_LINKS[code] : undefined;
  return portal ? { href: portal, external: true } : undefined;
}

/** Version mismatch between the deployed API and this frontend; null when the API reports no version. */
export function versionMismatch(backendVersion: unknown, appVersion: string): { backend: string; app: string; differs: boolean } | null {
  if (typeof backendVersion !== "string" || !backendVersion) return null;
  return { backend: backendVersion, app: appVersion, differs: backendVersion !== appVersion };
}

/** Links from readiness data must stay inside the existing settings routes. */
export function healthFixHref(href: string | undefined): string | undefined {
  if (!href || !/^\/admin(?:\/|$)/.test(href) || href.includes("\\")) return undefined;
  const pathname = href.split("?")[0];
  return ADMIN_NAV.some(group => group.items.some(item => item.path.split("?")[0] === pathname)) ? href : undefined;
}

export function hasTruncatedHealthItems(count: number | undefined, itemCount: number): boolean {
  return itemCount > 0 && count !== undefined && itemCount < count;
}

export interface SetupPriorityTask { id: string; checkId: string; itemId?: string; name?: string; issue?: string; href: string; priority: number }

/** Saved-data problems come before optional improvements; unknown is never urgent. */
export function setupPriorityTasks(report: TenantHealthOverview, limit = 3): SetupPriorityTask[] {
  const priorities: Record<string, number> = { profile: 0, activeTrips: 1, fortnox: 2, vesselSetup: 3, crewRecords: 4, checklistAdoption: 4, cancellationDefaults: 5, activeContent: 6, equipmentRecords: 8 };
  const tasks: SetupPriorityTask[] = [];
  for (const category of report.categories) for (const check of category.checks) {
    if (check.status !== "attention" && check.status !== "failing") continue;
    const records = check.items?.length ? check.items : [undefined];
    for (const item of records) {
      const href = healthFixHref(item?.fixHref ?? check.fixHref);
      if (!href) continue;
      const issues = item?.issues ?? [];
      const issue = issues.find(value => value === "missing_name" || value === "missing_description")
        ?? issues.find(value => /^missing_(en|sv)_/.test(value)) ?? issues[0];
      const priority = issue === "missing_name" || issue === "missing_description" ? 0
        : issue?.startsWith("missing_") ? 5 : issue?.startsWith("suggestion_") ? 8 : priorities[check.id] ?? 7;
      tasks.push({ id: `${check.id}:${item?.id ?? "setup"}`, checkId: check.id, itemId: item?.id, name: item?.name, issue, href, priority: check.status === "failing" ? -1 : priority });
    }
  }
  const seen = new Set<string>();
  return tasks.sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id)).filter(task => {
    if (seen.has(task.href)) return false;
    seen.add(task.href); return true;
  }).slice(0, limit);
}

export function tripPublicationCounts(report: TenantHealthOverview, stale = false): Array<{ id: string; count: number | null }> {
  const checks = report.categories.find(category => category.id === "tripsBooking")?.checks ?? [];
  return ["activeTrips", "inactiveTrips", "archivedTrips"].map(id => {
    const check = checks.find(value => value.id === id);
    return { id, count: stale || !check || check.status === "unknown" || check.count === undefined ? null : check.count };
  });
}
